import styled, { css } from "styled-components";

interface ButtonProps {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
}

const ButtonStyled = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.border.borderRadiusLarge};
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  transition: ${(props) => props.theme.transitions.normal};
  border: 2px solid transparent;
  min-height: 44px;
  min-width: 44px;
  user-select: none;
  position: relative;
  overflow: hidden;

  /* Tamaños */
  ${(props) => {
    switch (props.size) {
      case "small":
        return css`
          padding: ${props.theme.spacing.sm} ${props.theme.spacing.md};
          font-size: ${props.theme.fonts.textFontSizeS};
          min-height: 36px;
        `;
      case "large":
        return css`
          padding: ${props.theme.spacing.md} ${props.theme.spacing.xl};
          font-size: ${props.theme.fonts.textFontSizeL};
          min-height: 52px;
        `;
      default: // medium
        return css`
          padding: ${props.theme.spacing.md} ${props.theme.spacing.lg};
          font-size: ${props.theme.fonts.textFontSizeM};
          min-height: 44px;
        `;
    }
  }}

  /* Variantes */
  ${(props) => {
    switch (props.variant) {
      case "secondary":
        return css`
          background-color: ${props.theme.colour.interactive.secondary};
          color: ${props.theme.colour.text.inverse};
          &:hover:not(:disabled) {
            background-color: ${props.theme.colour.interactive.hover};
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadows.medium};
          }
        `;
      case "outline":
        return css`
          background-color: transparent;
          color: ${props.theme.colour.brand.primary};
          border-color: ${props.theme.colour.brand.primary};
          &:hover:not(:disabled) {
            background-color: ${props.theme.colour.brand.primary};
            color: ${props.theme.colour.text.inverse};
            transform: translateY(-1px);
          }
        `;
      case "ghost":
        return css`
          background-color: transparent;
          color: ${props.theme.colour.brand.primary};
          &:hover:not(:disabled) {
            background-color: ${props.theme.colour.state.selected};
            transform: translateY(-1px);
          }
        `;
      default: // primary
        return css`
          background-color: ${props.theme.colour.interactive.primary};
          color: ${props.theme.colour.text.inverse};
          &:hover:not(:disabled) {
            background-color: ${props.theme.colour.interactive.hover};
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadows.medium};
          }
        `;
    }
  }}

  /* Ancho completo */
  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}

  /* Estados */
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${(props) => props.theme.shadows.small};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colour.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none !important;
    box-shadow: none !important;
    background-color: ${(props) => props.theme.colour.interactive.disabled};
    color: ${(props) => props.theme.colour.text.disabled};
  }

  /* Efecto de ripple */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.3s, height 0.3s;
  }

  &:active::before {
    width: 300px;
    height: 300px;
  }

  /* Iconos dentro del botón */
  svg {
    width: 1em;
    height: 1em;
    flex-shrink: 0;
  }
`;

export default ButtonStyled;
